import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import {
  exportDepositsReport,
  getDepositCurrentBalance,
  getDepositDetail,
  getDepositsReport,
  getSalesOrderDeposits,
  type DepositDetailQueryKeyEntries,
  type DepositOrganizationScope,
  type DepositsReportExportRequest,
  type DepositsReportQueryKeyEntries,
  type SalesOrderDepositsQueryKeyEntries,
} from './deposits.api'
import type { DepositDetailSearch, DepositsReportFilters } from './deposits.schema'

/** Route params arrive as strings; ids from payloads are numbers. */
type DepositIdInput = number | string

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface DepositsReportScope extends DepositOrganizationScope {
  filters?: DepositsReportFilters | undefined
}

interface DepositDetailScope extends DepositOrganizationScope {
  depositId?: DepositIdInput
  context?: DepositDetailSearch | undefined
}

interface SalesOrderDepositsScope extends DepositOrganizationScope {
  salesOrderId?: SalesOrderIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type DepositsReportQueryKey = [AdminQueryRoot & DepositsReportQueryKeyEntries]
type DepositDetailQueryKey = [AdminQueryRoot & DepositDetailQueryKeyEntries]
type SalesOrderDepositsQueryKey = [AdminQueryRoot & SalesOrderDepositsQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type DepositQueryParams<TOptions> = OrganizationScope &
  Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const depositQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'deposit' }, { organizationId }),
  ],
  report: ({ organizationId, filters }: DepositsReportScope): DepositsReportQueryKey =>
    buildScopedAdminQueryKey(depositQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'report',
      filters,
    }),
  detail: ({ organizationId, depositId, context }: DepositDetailScope): DepositDetailQueryKey =>
    buildScopedAdminQueryKey(depositQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
      depositId: Number(depositId),
      context,
    }),
  currentBalance: ({ organizationId }: DepositOrganizationScope): ScopedAdminQueryKey =>
    buildScopedAdminQueryKey(depositQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'currentBalance',
    }),
  salesOrder: ({
    organizationId,
    salesOrderId,
  }: SalesOrderDepositsScope): SalesOrderDepositsQueryKey =>
    buildScopedAdminQueryKey(depositQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'salesOrder',
      salesOrderId: Number(salesOrderId),
    }),
}

export const depositQueries = {
  report: ({ organizationId, filters }: DepositsReportScope) =>
    indexTableQueryOptions({
      queryKey: depositQueryKeys.report({ organizationId, filters }),
      queryFn: getDepositsReport,
    }),
  detail: ({ organizationId, depositId, context }: DepositDetailScope) =>
    queryOptions({
      queryKey: depositQueryKeys.detail({ organizationId, depositId, context }),
      queryFn: getDepositDetail,
    }),
  currentBalance: ({ organizationId }: DepositOrganizationScope) =>
    queryOptions({
      queryKey: depositQueryKeys.currentBalance({ organizationId }),
      queryFn: getDepositCurrentBalance,
      staleTime: 60_000,
      retry: false,
    }),
  salesOrder: ({ organizationId, salesOrderId }: SalesOrderDepositsScope) =>
    queryOptions({
      queryKey: depositQueryKeys.salesOrder({ organizationId, salesOrderId }),
      queryFn: getSalesOrderDeposits,
    }),
}

export function useDepositsReport(
  filters?: DepositsReportFilters,
  { organizationId, ...params }: DepositQueryParams<ReturnType<typeof depositQueries.report>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...depositQueries.report({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useDepositDetail(
  depositId: DepositIdInput,
  context?: DepositDetailSearch,
  { organizationId, ...params }: DepositQueryParams<ReturnType<typeof depositQueries.detail>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...depositQueries.detail({ organizationId: resolvedOrganizationId, depositId, context }),
    ...params,
  })
}

export function useDepositCurrentBalance({
  organizationId,
  ...params
}: DepositQueryParams<ReturnType<typeof depositQueries.currentBalance>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...depositQueries.currentBalance({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSalesOrderDeposits(
  salesOrderId: SalesOrderIdInput,
  {
    organizationId,
    ...params
  }: DepositQueryParams<ReturnType<typeof depositQueries.salesOrder>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...depositQueries.salesOrder({ organizationId: resolvedOrganizationId, salesOrderId }),
    ...params,
  })
}

export function useExportDepositsReportMutation() {
  return useMutation({
    mutationFn: ({ organizationId, filters }: DepositsReportExportRequest) =>
      exportDepositsReport({ organizationId, filters }),
  })
}
